import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants/networking.constants';
import { AlertSeverityEnum, UseMutationsParams } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { Cargos, CargosPagingRes } from '@/shared/interfaces/erp/empleados';

const { get, post, put, remove } = erpApi();

///* tanSctack query
export const useFetchCargos = (params?: getCargosParams, enabled = true) => {
  return useQuery({
    queryKey: ['Cargos', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getCargos(params);
    },
    enabled,
  });
};

export const useGetCargo = (id: string) => {
  return useQuery({
    queryKey: ['Cargos', id],
    queryFn: async () => {
      return getCargo(id);
    },
  });
};

export const useCreateCargos = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createCargos,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Cargos'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Cargo creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};
export const useUpdateCargos = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateCargos,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Cargos'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Cargo actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useRemoveCargos = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: removeCargos,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Cargos'] });
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
  });
};

///* axios
export type getCargosParams = Partial<Cargos> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};

export type CreateCargosParams = Omit<Cargos, 'id_cargo'>;
export type UpdateCargosParams = {
  id: string;
  data: CreateCargosParams;
};

export const getCargos = async (params?: getCargosParams) => {
  const stateParams = { ...params };
  const queryParams = getUrlParamsFetch(stateParams);
  return get<CargosPagingRes>(`${urlBackend}api/v1/cargo/?${queryParams}`, true);
};

export const getCargo = async (id: string) => {
  try {
    return await get<Cargos>(`${urlBackend}api/v1/cargo/${id}/`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createCargos = async (data: CreateCargosParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<Cargos>(`${urlBackend}api/v1/cargo/`, data, true);
};

export const updateCargos = async ({ id, data }: UpdateCargosParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<Cargos>(`${urlBackend}api/v1/cargo/${id}/`, data, true);
};

export const removeCargos = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<Cargos>(`${urlBackend}api/v1/cargo/${id}/`, true);

  setAlert(true, res.message, res.status);
  return res;
};